import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowLeft,
  Bath,
  Bed,
  Gem,
  Globe,
  Lock,
  MapPin,
  Maximize,
  Menu,
  Target,
  TrendingUp,
  X
} from 'lucide-react';
import './LandingPage.css';

const LandingPage = () => {
  const prefersReducedMotion = useReducedMotion();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [featuredOffers, setFeaturedOffers] = React.useState([]);
  const [loadingFeaturedOffers, setLoadingFeaturedOffers] = React.useState(true);

  const { scrollYProgress } = useScroll();
  const heroShift = useTransform(scrollYProgress, [0, 0.4], [0, prefersReducedMotion ? 0 : -60]);

  React.useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  React.useEffect(() => {
    const pickImage = (offer) => {
      const candidate = offer?.coverImage || offer?.images?.[0] || '';
      if (typeof candidate === 'string' && /^https?:\/\//i.test(candidate)) return candidate;
      if (candidate && typeof candidate === 'object' && typeof candidate.url === 'string') return candidate.url;
      return '';
    };

    const mapOffer = (offer, idx) => ({
      id: offer._id || `public-${idx}`,
      image: pickImage(offer),
      detailsLink: `/register?intent=view-offer&offerId=${encodeURIComponent(offer._id || `public-${idx}`)}`,
      type: offer.offerType === 'rent' ? 'إيجار' : 'بيع',
      title: offer.title || 'عرض عقاري',
      location: [offer.city, offer.district].filter(Boolean).join('، ') || 'المملكة العربية السعودية',
      price: offer.price ? `${Number(offer.price).toLocaleString('ar-SA')} ريال` : 'على السوم',
      beds: offer.rooms || 0,
      baths: offer.bathrooms || 0,
      area: offer.area ? `${offer.area} م²` : '-'
    });

    const getAuthToken = () => (
      localStorage.getItem('msk_user_access_token') ||
      localStorage.getItem('msk_aqar_access_token') ||
      ''
    );

    const fetchJson = async (url, options = {}) => {
      const response = await fetch(url, options);
      if (!response.ok) return null;
      return response.json();
    };

    const loadPublicOffers = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        let mapped = [];

        const marketResponse = await fetch(`${apiBase}/public/market/offers?limit=6`);
        if (marketResponse.ok) {
          const marketJson = await marketResponse.json();
          const marketOffers = marketJson?.data?.offers || [];
          mapped = marketOffers.slice(0, 6).map(mapOffer).filter((item) => item.image);
        }

        if (mapped.length === 0) {
          const token = getAuthToken();
          if (token) {
            const networkJson = await fetchJson(`${apiBase}/user/network/feed?page=1&limit=30`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const networkOffers = networkJson?.data?.offers || [];
            mapped = networkOffers
              .filter((offer) => offer?.coverImage || (offer?.images && offer.images.length > 0))
              .slice(0, 6)
              .map(mapOffer)
              .filter((item) => item.image);
          }
        }

        if (mapped.length === 0) {
          const token = getAuthToken();
          if (token) {
            const userOffersJson = await fetchJson(`${apiBase}/user/offers?page=1&limit=40`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const userOffers = userOffersJson?.data?.offers || [];
            mapped = userOffers
              .filter((offer) => offer?.coverImage || (offer?.images && offer.images.length > 0))
              .slice(0, 6)
              .map(mapOffer)
              .filter((item) => item.image);
          }
        }

        if (mapped.length === 0) {
          const params = new URLSearchParams(window.location.search);
          const slug = params.get('catalog') || process.env.REACT_APP_PUBLIC_CATALOG_SLUG;
          if (slug) {
            const catalogResponse = await fetch(`${apiBase}/public/catalog/${encodeURIComponent(slug)}`);
            if (catalogResponse.ok) {
              const catalogJson = await catalogResponse.json();
              const catalogOffers = catalogJson?.data?.catalog?.offers || [];
              mapped = catalogOffers.slice(0, 6).map(mapOffer).filter((item) => item.image);
            }
          }
        }

        setFeaturedOffers(mapped);
      } catch (_) {
        setFeaturedOffers([]);
      } finally {
        setLoadingFeaturedOffers(false);
      }
    };

    loadPublicOffers();
  }, []);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: prefersReducedMotion ? 0 : 0.07 }
    }
  };

  const revealUp = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.2, 0.65, 0.3, 0.9] }
    }
  };

  const pillars = [
    { icon: <Target size={18} />, label: 'مطابقة تلقائية' },
    { icon: <Globe size={18} />, label: 'شبكة مسوّقين' },
    { icon: <Lock size={18} />, label: 'خصوصية كاملة' },
    { icon: <TrendingUp size={18} />, label: 'متابعة لحظية' },
  ];

  const featureCards = [
    {
      icon: <Target size={20} />,
      title: 'مطابقة العرض بالطلب',
      text: 'النظام يطابق تلقائياً حسب السعر والمساحة والموقع.',
    },
    {
      icon: <TrendingUp size={20} />,
      title: 'متابعة كل فرصة',
      text: 'حالة الصفقة وسجل التواصل في مكان واحد.',
    },
    {
      icon: <Globe size={20} />,
      title: 'شبكة مسوّقين',
      text: 'شارك عروضك مع المسوّقين المناسبين فقط.',
    },
    {
      icon: <Lock size={20} />,
      title: 'تحكّم بالخصوصية',
      text: 'حدّد الحقول المرئية قبل مشاركة أي عرض.',
    },
    {
      icon: <Gem size={20} />,
      title: 'مصمّم للسوق السعودي',
      text: 'واجهة عربية تناسب طريقة عملك اليومية.',
    },
  ];

  const showTypeBadge = React.useMemo(() => {
    const uniqueTypes = new Set(featuredOffers.map((offer) => offer.type).filter(Boolean));
    return uniqueTypes.size > 1;
  }, [featuredOffers]);

  return (
    <div className="landing-page" dir="rtl">
      <motion.header
        className="landing-header"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="header-container">
          <Link to="/" className="brand-block" onClick={() => setMobileMenuOpen(false)}>
            <div className="brand-icon">
              <img src="/mesk-brand-icon.svg" alt="شعار مسك" className="brand-logo-img" />
            </div>
            <span className="brand-title">مسك</span>
          </Link>

          <nav className={`header-nav ${mobileMenuOpen ? 'open' : ''}`}>
            <a href="#features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>المزايا</a>
            <a href="#preview" className="nav-link" onClick={() => setMobileMenuOpen(false)}>العروض</a>
            <a href="#contact" className="nav-link" onClick={() => setMobileMenuOpen(false)}>التواصل</a>
            <div className="mobile-nav-actions">
              <Link to="/login" className="header-link-btn" onClick={() => setMobileMenuOpen(false)}>تسجيل الدخول</Link>
              <Link to="/register" className="header-solid-btn" onClick={() => setMobileMenuOpen(false)}>إنشاء حساب</Link>
            </div>
          </nav>

          <div className="header-actions">
            <Link to="/login" className="header-link-btn">تسجيل الدخول</Link>
            <Link to="/register" className="header-solid-btn">ابدأ الآن</Link>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label="فتح القائمة"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {mobileMenuOpen && <button className="mobile-nav-backdrop" onClick={() => setMobileMenuOpen(false)} aria-label="إغلاق القائمة" />}
      </motion.header>

      <section className="hero-section">
        <div className="hero-container">
          <motion.div
            className="hero-content"
            variants={container}
            initial="hidden"
            animate="show"
          >
            <motion.h1 variants={revealUp} className="hero-title">
              نظام واحد لتسويقك العقاري
            </motion.h1>

            <motion.p variants={revealUp} className="hero-description">
              أضف عروضك، استقبل الطلبات، طابق، شارك مع شبكتك، وتابع كل صفقة.
            </motion.p>

            <motion.div variants={revealUp} className="hero-cta-row">
              <Link to="/register" className="hero-primary-btn">
                ابدأ مجاناً
                <ArrowLeft size={18} />
              </Link>
              <Link to="/login" className="hero-secondary-btn">تسجيل الدخول</Link>
            </motion.div>

            <motion.div variants={revealUp} className="hero-pillars">
              {pillars.map((p, i) => (
                <div key={i} className="hero-pillar">
                  {p.icon}
                  <span>{p.label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div className="hero-visual" style={{ y: heroShift }}>
            <div className="hero-image-frame">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=80"
                alt="واجهة عقار فاخر"
                className="hero-image"
                loading="eager"
              />
              <div className="hero-image-overlay" />
            </div>
          </motion.div>
        </div>
      </section>

      <section id="features" className="features-section">
        <div className="features-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.35 }}
          >
            <h2 className="section-title">أدوات تشتغل معك، مو عليك</h2>
            <p className="section-subtitle">كل شي تحتاجه لإدارة تسويقك العقاري في مكان واحد.</p>
          </motion.div>

          <motion.div
            className="features-grid"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            {featureCards.map((card, index) => (
              <motion.article
                key={index}
                className="feature-card"
                variants={revealUp}
                whileHover={prefersReducedMotion ? undefined : { y: -4 }}
              >
                <div className="feature-icon">{card.icon}</div>
                <h3 className="feature-title">{card.title}</h3>
                <p className="feature-text">{card.text}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="preview" className="properties-section">
        <div className="properties-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.25 }}
          >
            <h2 className="section-title">عروض من المنصة</h2>
            <p className="section-subtitle">نماذج حقيقية لشكل العروض داخل مسك.</p>
          </motion.div>

          <motion.div
            className="offers-grid"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
          >
            {featuredOffers.length > 0 ? (
              featuredOffers.map((property, index) => (
                <motion.div key={property.id || index} variants={revealUp}>
                  <Link to={property.detailsLink || '/register'} className="offer-card">
                    <div className="offer-thumb">
                      <img src={property.image} alt={property.title} loading="lazy" />
                      {showTypeBadge ? (
                        <span className="offer-type-badge">{property.type}</span>
                      ) : null}
                    </div>
                    <div className="offer-info">
                      <h4 className="offer-title">{property.title}</h4>
                      <div className="offer-price">{property.price}</div>
                      <div className="offer-meta">
                        <span><MapPin size={12} /> {property.location}</span>
                        <span><Maximize size={12} /> {property.area}</span>
                        <span><Bed size={12} /> {property.beds || '-'}</span>
                        <span><Bath size={12} /> {property.baths || '-'}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="offers-empty">
                {loadingFeaturedOffers
                  ? 'جاري تحميل العروض...'
                  : 'لا توجد عروض متاحة حالياً.'}
              </div>
            )}
          </motion.div>

          <motion.div
            className="section-cta"
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.35 }}
          >
            <Link to="/offer-form" className="hero-secondary-btn">
              إضافة عرض عبر النموذج
              <ArrowLeft size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="cta-section" id="contact">
        <div className="cta-container">
          <motion.div
            className="cta-content"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.h2 variants={revealUp} className="cta-title">
              جاهز ترتّب شغلك العقاري؟
            </motion.h2>
            <motion.p variants={revealUp} className="cta-desc">
              سجّل الآن وابدأ بإضافة أول عرض لك.
            </motion.p>
            <motion.div variants={revealUp} className="cta-buttons">
              <Link to="/register" className="cta-primary-btn">
                إنشاء حساب
                <ArrowLeft size={18} />
              </Link>
              <Link to="/login" className="cta-secondary-btn">تسجيل الدخول</Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="brand-block">
                <div className="brand-icon"><img src="/mesk-brand-icon.svg" alt="شعار مسك" className="brand-logo-img" /></div>
                <span className="brand-title">مسك</span>
              </div>
              <p className="footer-desc">نظام تسويق عقاري للمسوّقين في السعودية.</p>
            </div>

            <div className="footer-links">
              <div className="footer-col">
                <h4>المنصة</h4>
                <Link to="/login">تسجيل الدخول</Link>
                <Link to="/register">إنشاء حساب</Link>
                <Link to="/offer-form">إضافة عرض</Link>
              </div>
              <div className="footer-col">
                <h4>الأدوات</h4>
                <Link to="/request-form">إضافة طلب</Link>
                <Link to="/login">لوحة التحكم</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© ٢٠٢٦ مسك. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
